import { useEffect, useMemo, useState } from "react"
import { Eye, History, RotateCcw } from "lucide-react"
import toast from "react-hot-toast"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
} from "@aix/ui"

import { trpc } from "../lib/trpc"

interface SEORevisionHistoryProps {
  productKey: string
  slug: string
  currentContentHash: string
  onRestored: () => void
}

export default function SEORevisionHistory({
  productKey,
  slug,
  currentContentHash,
  onRestored,
}: SEORevisionHistoryProps) {
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const [previewRevisionId, setPreviewRevisionId] = useState<string | null>(null)

  const revisions = trpc.seo.pages.revisions.list.useQuery({ productKey, slug, limit: 20 })
  const rows = useMemo(() => revisions.data?.rows ?? [], [revisions.data?.rows])
  useEffect(() => {
    if (!selectedRevisionId && rows[0]) setSelectedRevisionId(rows[0].id)
  }, [rows, selectedRevisionId])

  const selected = trpc.seo.pages.revisions.get.useQuery(
    { productKey, slug, revisionId: selectedRevisionId ?? "" },
    { enabled: Boolean(selectedRevisionId) },
  )
  const historicalPreview = trpc.seo.pages.preview.useQuery(
    { productKey, slug, revisionId: previewRevisionId ?? undefined },
    { enabled: Boolean(previewRevisionId) },
  )
  const restoreMutation = trpc.seo.pages.revisions.restore.useMutation({
    onError: (error) => toast.error(error.message || "Revision restore failed"),
    onSuccess: (result) => {
      toast.success(
        result.restored ? "Historical revision restored as a new draft" : "That revision is already current",
      )
      setRestoreOpen(false)
      setPreviewRevisionId(null)
      onRestored()
    },
  })

  if (revisions.isLoading) return <p className="text-xs text-muted-foreground">Loading revision history…</p>
  if (revisions.error)
    return <p className="text-xs text-destructive">Revision history unavailable: {revisions.error.message}</p>
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No revisions have been recorded yet.</p>

  return (
    <section className="space-y-3 rounded-md border border-border p-3">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4" />
        <h3 className="text-sm font-semibold">Revision history</h3>
        <Badge variant="secondary">
          {rows.length}
          {revisions.data?.nextCursor ? "+" : ""}
        </Badge>
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(170px,0.8fr)_minmax(0,1.2fr)]">
        <div className="max-h-72 space-y-1 overflow-auto">
          {rows.map((revision) => (
            <button
              key={revision.id}
              type="button"
              onClick={() => {
                setSelectedRevisionId(revision.id)
                setPreviewRevisionId(null)
              }}
              className={`w-full rounded border p-2 text-left text-xs transition-colors hover:bg-muted ${selectedRevisionId === revision.id ? "border-primary bg-muted" : "border-border"}`}
            >
              <span className="block font-medium">{new Date(revision.createdAt).toLocaleString()}</span>
              <span className="mt-0.5 line-clamp-2 block text-muted-foreground">
                {revision.note || "No change note"}
              </span>
              {revision.contentHash === currentContentHash ? (
                <Badge className="mt-1" variant="success">
                  Current
                </Badge>
              ) : null}
            </button>
          ))}
        </div>

        <div className="min-w-0">
          {selected.isLoading ? <p className="text-xs text-muted-foreground">Comparing revision…</p> : null}
          {selected.error ? <p className="text-xs text-destructive">{selected.error.message}</p> : null}
          {selected.data ? (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setPreviewRevisionId(selected.data.revision.id)}>
                  <Eye className="mr-1.5 h-4 w-4" /> Preview this revision
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={selected.data.revision.contentHash === currentContentHash}
                  onClick={() => setRestoreOpen(true)}
                >
                  <RotateCcw className="mr-1.5 h-4 w-4" /> Restore as new draft
                </Button>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Changed fields</p>
                {selected.data.fieldDiff.length ? (
                  <ul className="mt-1 space-y-1 text-xs">
                    {selected.data.fieldDiff.map((difference) => (
                      <li key={difference.field} className="rounded bg-muted px-2 py-1">
                        <span className="font-mono">{difference.field}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">No content differences from the current draft.</p>
                )}
              </div>
              <p
                className="truncate font-mono text-[10px] text-muted-foreground"
                title={selected.data.revision.contentHash}
              >
                {selected.data.revision.contentHash}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {previewRevisionId ? (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold">Historical preview</p>
            <Button size="sm" variant="ghost" onClick={() => setPreviewRevisionId(null)}>
              Close
            </Button>
          </div>
          {historicalPreview.error ? (
            <p className="text-xs text-destructive">{historicalPreview.error.message}</p>
          ) : null}
          {historicalPreview.isLoading ? (
            <p className="text-xs text-muted-foreground">Rendering historical revision…</p>
          ) : null}
          {historicalPreview.data ? (
            <iframe
              title={`Historical preview for ${slug}`}
              srcDoc={historicalPreview.data.html}
              sandbox="allow-popups"
              className="h-[480px] w-full rounded border border-border bg-white"
            />
          ) : null}
        </div>
      ) : null}

      <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this historical revision?</AlertDialogTitle>
            <AlertDialogDescription>
              This preserves all history and creates a new current draft from the selected revision. Quality gates must
              run again before publication.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!selectedRevisionId) return
                restoreMutation.mutate({
                  productKey,
                  slug,
                  revisionId: selectedRevisionId,
                  expectedContentHash: currentContentHash,
                  note: `Restored revision ${selectedRevisionId} in AIX Command Center`,
                })
              }}
            >
              Restore revision
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
